import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileText,
  Calendar,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import { api } from '../../lib/api'

interface ScanDetails {
  id: number
  subject: string | null
  title: string | null
  grade_detected: number | null
  created_at: string
}

interface ExpectedWork {
  id: number
  title: string
  course: string
  course_id: number | null
  start_at: string | null
  event_type: string
  has_scan: boolean
  scan: ScanDetails | null
}

interface UnmatchedScan {
  id: number
  subject: string | null
  title: string | null
  scan_type: string | null
  grade_detected: number | null
  created_at: string
}

interface ReconciliationResponse {
  student: {
    id: number
    name: string
  }
  expected_work: ExpectedWork[]
  unmatched_scans: UnmatchedScan[]
  stats: {
    total_expected: number
    scans_received: number
    missing: number
    unmatched_scans: number
    completion_pct: number
  }
  date_range: {
    start: string
    end: string
    days: number
  }
}

interface Props {
  studentId: number
  studentName: string
}

export default function ReconciliationView({ studentId, studentName }: Props) {
  const [days, setDays] = useState(7)
  const [showMatched, setShowMatched] = useState(false)
  const [showUnmatched, setShowUnmatched] = useState(true)

  const { data, isLoading } = useQuery<ReconciliationResponse>({
    queryKey: ['reconciliation', studentId, days],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${studentId}/reconciliation?days=${days}`)
      return response.data
    },
  })

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'No date'
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
  }

  const matchedItems = data?.expected_work.filter((item) => item.has_scan) || []
  const missingItems = data?.expected_work.filter((item) => !item.has_scan) || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-400" />
            Work Reconciliation
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Expected work vs scans received for {studentName}
          </p>
        </div>

        {/* Date range selector */}
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="bg-surface-card border border-gray-700/50 rounded-lg px-3 py-2 text-sm"
        >
          <option value={3}>Last 3 days</option>
          <option value={7}>Last week</option>
          <option value={14}>Last 2 weeks</option>
          <option value={30}>Last month</option>
        </select>
      </div>

      {/* Stats cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-lg font-bold">{data.stats.total_expected}</p>
              <p className="text-xs text-gray-400">Expected</p>
            </div>
          </div>

          <div className="card flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-lg font-bold">{data.stats.scans_received}</p>
              <p className="text-xs text-gray-400">Have Scans</p>
            </div>
          </div>

          <div className="card flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              data.stats.missing > 0 ? 'bg-red-500/20' : 'bg-gray-700'
            }`}>
              <XCircle className={`w-5 h-5 ${data.stats.missing > 0 ? 'text-red-400' : 'text-gray-400'}`} />
            </div>
            <div>
              <p className={`text-lg font-bold ${data.stats.missing > 0 ? 'text-red-400' : ''}`}>
                {data.stats.missing}
              </p>
              <p className="text-xs text-gray-400">Missing</p>
            </div>
          </div>

          <div className="card flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="text-lg font-bold">{data.stats.unmatched_scans}</p>
              <p className="text-xs text-gray-400">Orphan Scans</p>
            </div>
          </div>
        </div>
      )}

      {/* Progress bar */}
      {data && data.stats.total_expected > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Completion Rate</span>
            <span className="text-sm font-bold text-green-400">{data.stats.completion_pct}%</span>
          </div>
          <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-500"
              style={{ width: `${data.stats.completion_pct}%` }}
            />
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
        </div>
      ) : data ? (
        <div className="space-y-6">
          {/* Missing scans section */}
          {missingItems.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-bold flex items-center gap-2 text-red-400">
                <XCircle className="w-5 h-5" />
                Missing Scans ({missingItems.length})
              </h3>

              <div className="space-y-2">
                {missingItems.map((item) => (
                  <div
                    key={item.id}
                    className="card p-4 border-red-500/30 bg-red-900/10"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">{item.title}</h4>
                        <div className="flex items-center gap-2 mt-1 text-sm text-gray-400">
                          <span className="px-2 py-0.5 rounded bg-gray-700 text-xs">
                            {item.course}
                          </span>
                          <span>{formatDate(item.start_at)}</span>
                        </div>
                      </div>
                      <div className="px-3 py-1 rounded-full bg-red-500/20 text-red-400 text-sm font-medium">
                        No scan
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Matched items section (collapsible) */}
          {matchedItems.length > 0 && (
            <div className="space-y-3">
              <button
                onClick={() => setShowMatched(!showMatched)}
                className="w-full flex items-center justify-between text-lg font-bold text-green-400"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  Matched ({matchedItems.length})
                </div>
                {showMatched ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>

              {showMatched && (
                <div className="space-y-2">
                  {matchedItems.map((item) => (
                    <div
                      key={item.id}
                      className="card p-4 border-green-500/30 bg-green-900/10"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{item.title}</h4>
                          <div className="flex items-center gap-2 mt-1 text-sm text-gray-400">
                            <span className="px-2 py-0.5 rounded bg-gray-700 text-xs">
                              {item.course}
                            </span>
                            <span>{formatDate(item.start_at)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {item.scan?.grade_detected !== null && (
                            <span className="text-lg font-bold text-green-400">
                              {item.scan?.grade_detected}%
                            </span>
                          )}
                          <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                            <CheckCircle2 className="w-4 h-4 text-green-400" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Unmatched scans section */}
          {data.unmatched_scans.length > 0 && (
            <div className="space-y-3">
              <button
                onClick={() => setShowUnmatched(!showUnmatched)}
                className="w-full flex items-center justify-between text-lg font-bold text-orange-400"
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Orphan Scans ({data.unmatched_scans.length})
                </div>
                {showUnmatched ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>

              {showUnmatched && (
                <div className="space-y-2">
                  {data.unmatched_scans.map((scan) => (
                    <div
                      key={scan.id}
                      className="card p-4 border-orange-500/30 bg-orange-900/10"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">
                            {scan.title || scan.subject || 'Untitled Scan'}
                          </h4>
                          <div className="flex items-center gap-2 mt-1 text-sm text-gray-400">
                            {scan.subject && (
                              <span className="px-2 py-0.5 rounded bg-gray-700 text-xs">
                                {scan.subject}
                              </span>
                            )}
                            {scan.scan_type && (
                              <span className="px-2 py-0.5 rounded bg-gray-700 text-xs">
                                {scan.scan_type}
                              </span>
                            )}
                            <span>{formatDate(scan.created_at)}</span>
                          </div>
                        </div>
                        {scan.grade_detected !== null && (
                          <span className="text-lg font-bold text-orange-400">
                            {scan.grade_detected}%
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-sm text-gray-400 italic">
                These scans haven't been matched to any expected work.
                {studentName} can match them in the Quest app.
              </p>
            </div>
          )}

          {/* Empty state */}
          {data.expected_work.length === 0 && (
            <div className="card text-center py-12">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-600" />
              <h3 className="text-lg font-medium mb-1">No Upcoming Work</h3>
              <p className="text-gray-400 text-sm">
                No expected work found for the selected period.
              </p>
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}
